import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const [customers, setCustomers] = useState([]);
  const [chats, setChats] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [socialAccounts, setSocialAccounts] = useState([]);
  const [aiSettings, setAiSettings] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [selectedChat, setSelectedChat] = useState(null);
  const [products, setProducts] = useState([]);
  const [campaignGroups, setCampaignGroups] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch customers
  const fetchCustomers = useCallback(async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.campaign_id) params.append('campaign_id', filters.campaign_id);
      if (filters.search) params.append('search', filters.search);
      
      const res = await axios.get(`${API}/customers?${params}`);
      setCustomers(res.data);
      return res.data;
    } catch (err) {
      // Error handled by state
      setError(err.message);
      return [];
    }
  }, []);

  // Fetch chats
  const fetchChats = useCallback(async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.platform) params.append('platform', filters.platform);
      if (filters.campaign_id) params.append('campaign_id', filters.campaign_id);
      if (filters.customer_id) params.append('customer_id', filters.customer_id);
      
      const res = await axios.get(`${API}/chats?${params}`);
      setChats(res.data);
      return res.data;
    } catch (err) {
      // Error handled by state
      setError(err.message);
      return [];
    }
  }, []);

  // Fetch campaigns
  const fetchCampaigns = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/campaigns`);
      setCampaigns(res.data);
      return res.data;
    } catch (err) {
      // Error handled by state
      return [];
    }
  }, []);

  // Fetch social accounts
  const fetchSocialAccounts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/social-accounts`);
      setSocialAccounts(res.data);
      return res.data;
    } catch (err) {
      // Error handled by state
      return [];
    }
  }, []);

  // Fetch AI settings
  const fetchAiSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/ai-settings`);
      setAiSettings(res.data);
      return res.data;
    } catch (err) {
      // Error handled by state
      return null;
    }
  }, []);

  // Fetch analytics
  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/analytics`);
      setAnalytics(res.data);
      return res.data;
    } catch (err) {
      // Error handled by state
      return null;
    }
  }, []);

  // Create customer
  const createCustomer = async (customerData) => {
    try {
      const res = await axios.post(`${API}/customers`, customerData);
      await fetchCustomers();
      return res.data;
    } catch (err) {
      // Error handled by state
      throw err;
    }
  };

  // Update customer
  const updateCustomer = async (customerId, customerData) => {
    try {
      const res = await axios.put(`${API}/customers/${customerId}`, customerData);
      await fetchCustomers();
      return res.data;
    } catch (err) {
      // Error handled by state
      throw err;
    }
  };

  // Delete customer
  const deleteCustomer = async (customerId) => {
    try {
      await axios.delete(`${API}/customers/${customerId}`);
      await fetchCustomers();
    } catch (err) {
      // Error handled by state
      throw err;
    }
  };

  // Create campaign
  const createCampaign = async (campaignData) => {
    try {
      const res = await axios.post(`${API}/campaigns`, campaignData);
      await fetchCampaigns();
      return res.data;
    } catch (err) {
      // Error handled by state
      throw err;
    }
  };

  // Update campaign
  const updateCampaign = async (campaignId, campaignData) => {
    try {
      const res = await axios.put(`${API}/campaigns/${campaignId}`, campaignData);
      await fetchCampaigns();
      return res.data;
    } catch (err) {
      // Error handled by state
      throw err;
    }
  };

  // Delete campaign
  const deleteCampaign = async (campaignId) => {
    try {
      await axios.delete(`${API}/campaigns/${campaignId}`);
      await fetchCampaigns();
    } catch (err) {
      // Error handled by state
      throw err;
    }
  };

  // Create social account
  const createSocialAccount = async (accountData) => {
    try {
      const res = await axios.post(`${API}/social-accounts`, accountData);
      await fetchSocialAccounts();
      return res.data;
    } catch (err) {
      // Error handled by state
      throw err;
    }
  };

  // Connect social account
  const connectSocialAccount = async (accountId, accessToken) => {
    try {
      await axios.put(`${API}/social-accounts/${accountId}/connect?access_token=${accessToken}`);
      await fetchSocialAccounts();
    } catch (err) {
      // Error handled by state
      throw err;
    }
  };

  // Disconnect social account
  const disconnectSocialAccount = async (accountId) => {
    try {
      await axios.put(`${API}/social-accounts/${accountId}/disconnect`);
      await fetchSocialAccounts();
    } catch (err) {
      // Error handled by state
      throw err;
    }
  };

  // Delete social account
  const deleteSocialAccount = async (accountId) => {
    try {
      await axios.delete(`${API}/social-accounts/${accountId}`);
      await fetchSocialAccounts();
    } catch (err) {
      // Error handled by state
      throw err;
    }
  };

  // Update AI settings
  const updateAiSettings = async (settingsData) => {
    try {
      const res = await axios.put(`${API}/ai-settings`, settingsData);
      setAiSettings(res.data);
      return res.data;
    } catch (err) {
      // Error handled by state
      throw err;
    }
  };

  // Get chat messages
  const getChatMessages = async (chatId) => {
    try {
      const res = await axios.get(`${API}/chats/${chatId}/messages`);
      return res.data;
    } catch (err) {
      // Error handled by state
      return [];
    }
  };

  // Send message
  const sendMessage = async (chatId, messageData) => {
    try {
      const res = await axios.post(`${API}/chats/${chatId}/messages`, messageData);
      return res.data;
    } catch (err) {
      // Error handled by state
      throw err;
    }
  };

  // Mark chat as read
  const markChatRead = async (chatId) => {
    try {
      await axios.put(`${API}/chats/${chatId}/read`);
      await fetchChats();
    } catch (err) {
      // Error handled by state
    }
  };

  // Toggle chat AI
  const toggleChatAi = async (chatId, enabled) => {
    try {
      await axios.put(`${API}/chats/${chatId}/ai-toggle?enabled=${enabled}`);
      await fetchChats();
    } catch (err) {
      // Error handled by state
    }
  };

  // Chat with Noah (AI Assistant)
  const chatWithNoah = async (message, sessionId = 'default') => {
    try {
      const res = await axios.post(`${API}/ai-assistant`, { message, session_id: sessionId });
      return res.data;
    } catch (err) {
      // Error handled by state
      throw err;
    }
  };

  // Create chat
  const createChat = async (chatData) => {
    try {
      const res = await axios.post(`${API}/chats`, chatData);
      await fetchChats();
      return res.data;
    } catch (err) {
      // Error handled by state
      throw err;
    }
  };

  // ====== Products ======
  const fetchProducts = useCallback(async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.category) params.append('category', filters.category);
      if (filters.campaign_id) params.append('campaign_id', filters.campaign_id);
      const res = await axios.get(`${API}/products?${params}`);
      setProducts(res.data);
      return res.data;
    } catch (err) {
      // Error handled by state
      return [];
    }
  }, []);

  const createProduct = async (productData) => {
    try {
      const res = await axios.post(`${API}/products`, productData);
      await fetchProducts();
      return res.data;
    } catch (err) {
      // Error handled by state
      throw err;
    }
  };

  const updateProduct = async (productId, productData) => {
    try {
      const res = await axios.put(`${API}/products/${productId}`, productData);
      await fetchProducts();
      return res.data;
    } catch (err) {
      // Error handled by state
      throw err;
    }
  };

  const deleteProduct = async (productId) => {
    try {
      await axios.delete(`${API}/products/${productId}`);
      await fetchProducts();
    } catch (err) {
      // Error handled by state
      throw err;
    }
  };

  const generateImage = async (name, description, style) => {
    try {
      const res = await axios.post(`${API}/ai/generate-image`, {
        product_name: name,
        description,
        style: style || 'professional product photo'
      });
      return res.data;
    } catch (err) {
      // Error handled by state
      throw err;
    }
  };

  // Save AI Settings
  const saveAISettings = async (settingsData) => {
    return updateAiSettings(settingsData);
  };

  // Save Social Accounts (bulk)
  const saveSocialAccounts = (accounts) => {
    setSocialAccounts(accounts);
  };

  // ====== Campaign Groups ======
  const fetchCampaignGroups = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/campaign-groups`);
      setCampaignGroups(res.data);
      return res.data;
    } catch (err) {
      // Error handled by state
      return [];
    }
  }, []);

  const createCampaignGroup = async (groupData) => {
    try {
      const res = await axios.post(`${API}/campaign-groups`, groupData);
      await fetchCampaignGroups();
      await fetchCampaigns();
      return res.data;
    } catch (err) {
      // Error handled by state
      throw err;
    }
  };

  const updateCampaignGroup = async (groupId, groupData) => {
    try {
      const res = await axios.put(`${API}/campaign-groups/${groupId}`, groupData);
      await fetchCampaignGroups();
      await fetchCampaigns();
      return res.data;
    } catch (err) {
      // Error handled by state
      throw err;
    }
  };

  const deleteCampaignGroup = async (groupId) => {
    try {
      await axios.delete(`${API}/campaign-groups/${groupId}`);
      await fetchCampaignGroups();
      await fetchCampaigns();
    } catch (err) {
      // Error handled by state
      throw err;
    }
  };

  // ====== Notifications ======
  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications`);
      setNotifications(res.data);
      return res.data;
    } catch (err) {
      // Error handled by state
      return [];
    }
  }, []);

  const markNotificationRead = async (notifId) => {
    try {
      await axios.put(`${API}/notifications/${notifId}/read`);
      setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, read: true } : n));
    } catch (err) {
      // Error handled by state
    }
  };

  const markAllNotificationsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (err) {
      // Error handled by state
    }
  };

  // Seed demo data
  const seedDemoData = async () => {
    try {
      setLoading(true);
      await axios.post(`${API}/seed-data`);
      await Promise.all([
        fetchCustomers(),
        fetchChats(),
        fetchCampaigns(),
        fetchAnalytics()
      ]);
    } catch (err) {
      // Error handled by state
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchCustomers(),
        fetchChats(),
        fetchCampaigns(),
        fetchSocialAccounts(),
        fetchAiSettings(),
        fetchAnalytics(),
        fetchProducts(),
        fetchCampaignGroups(),
        fetchNotifications()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchCustomers, fetchChats, fetchCampaigns, fetchSocialAccounts, fetchAiSettings, fetchAnalytics, fetchProducts, fetchCampaignGroups, fetchNotifications]);

  // SSE for real-time notifications
  useEffect(() => {
    let eventSource;
    try {
      eventSource = new EventSource(`${API}/notifications/stream`);
      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type !== 'heartbeat') {
            setNotifications(prev => [data, ...prev]);
            // Refresh relevant data on notification
            if (data.type === 'new_message') fetchChats();
            if (data.type === 'new_customer') fetchCustomers();
          }
        } catch (e) { console.warn('SSE notification parse error:', e?.message); }
      };
      eventSource.onerror = () => {
        eventSource?.close();
      };
    } catch (e) { console.warn('SSE not supported:', e?.message); }
    return () => eventSource?.close();
  }, [fetchChats, fetchCustomers]);

  const value = {
    // State
    customers,
    chats,
    campaigns,
    socialAccounts,
    aiSettings,
    analytics,
    selectedChat,
    products,
    campaignGroups,
    notifications,
    loading,
    error,
    
    // Setters
    setSelectedChat,
    
    // Customer actions
    fetchCustomers,
    createCustomer,
    updateCustomer,
    deleteCustomer,
    
    // Chat actions
    fetchChats,
    createChat,
    getChatMessages,
    sendMessage,
    markChatRead,
    toggleChatAi,
    
    // Campaign actions
    fetchCampaigns,
    createCampaign,
    updateCampaign,
    deleteCampaign,
    
    // Social account actions
    fetchSocialAccounts,
    createSocialAccount,
    connectSocialAccount,
    disconnectSocialAccount,
    deleteSocialAccount,
    saveSocialAccounts,
    
    // Product actions
    fetchProducts,
    createProduct,
    updateProduct,
    deleteProduct,
    generateImage,
    
    // AI actions
    fetchAiSettings,
    updateAiSettings,
    saveAISettings,
    chatWithNoah,
    
    // Campaign Groups
    campaignGroups,
    fetchCampaignGroups,
    createCampaignGroup,
    updateCampaignGroup,
    deleteCampaignGroup,
    
    // Notifications
    fetchNotifications,
    markNotificationRead,
    markAllNotificationsRead,
    
    // Analytics
    fetchAnalytics,
    
    // Demo
    seedDemoData
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};
